import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { fetchData } from 'services/api/fetchData';

// The running timer of the current user as returned by the backend
export interface RunningTimer {
    id: number;
    start_datetime: string;
    assignee: number;
    time_type: { id: number; name: string } | null;
    project: { id: number; name: string } | null;
    job: { id: number; number: string | null } | null;
    ticket: { id: number; number: string | null; subject: string | null } | null;
    note: string | null;
}

interface TimerContextType {
    runningTimer: RunningTimer | null;
    timerLoaded: boolean;
    refreshTimer: () => Promise<void>;
    setRunningTimer: (timer: RunningTimer | null) => void;
}

const TimerContext = createContext<TimerContextType>(undefined!);

// Formats the elapsed seconds of a timer as h:mm:ss
export const formatElapsedTime = (startDatetime: string, now: number = Date.now()): string => {
    const totalSeconds = Math.max(Math.floor((now - new Date(startDatetime).getTime()) / 1000), 0);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Keeps the running timer of the current user available to the menu bar and the ticket page
export const TimerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isLoggedIn, handleLogout } = useAuthContext();
    const [runningTimer, setRunningTimer] = useState<RunningTimer | null>(null);
    const [timerLoaded, setTimerLoaded] = useState(false);

    // Keep the logout handler in a ref, its identity changes on every render
    const handleLogoutRef = useRef(handleLogout);
    handleLogoutRef.current = handleLogout;

    // Fetch the running timer of the current user
    const refreshTimer = useCallback(async () => {
        if (!isLoggedIn) {
            setRunningTimer(null);
            return;
        }
        try {
            const data = await fetchData({ apiUrl: 'get_running_timerow', handleLogout: handleLogoutRef.current });
            setRunningTimer(data?.running_timer ?? null);
        } catch (error) {
            console.error('Failed to load the running timer', error);
        } finally {
            setTimerLoaded(true);
        }
    }, [isLoggedIn]);

    // Load the running timer on login and keep it in sync every minute while the tab is visible
    useEffect(() => {
        refreshTimer();
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') refreshTimer();
        }, 60000);
        return () => window.clearInterval(interval);
    }, [refreshTimer]);

    return (
        <TimerContext.Provider value={{ runningTimer, timerLoaded, refreshTimer, setRunningTimer }}>
            {children}
        </TimerContext.Provider>
    );
};

// Returns the running timer context
export const useTimer = () => useContext(TimerContext);

// Returns the elapsed time of the running timer, updated every second
export const useElapsedTime = (startDatetime: string | null | undefined): string | null => {
    const [now, setNow] = useState(Date.now());

    // Tick every second while a timer runs
    useEffect(() => {
        if (!startDatetime) return;
        setNow(Date.now());
        const interval = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(interval);
    }, [startDatetime]);

    if (!startDatetime) return null;
    return formatElapsedTime(startDatetime, now);
};
